export interface RaptorSourceBaseNode {
  text: string;
  score: number;
}

type MetadataBase = Record<string, string | null | number>;

export interface RaptorEmailSourceNode extends RaptorSourceBaseNode {
  metadata: MetadataBase & {
    cc: string; // E.g. 'John Doe <john.doe@example.com>, Jane Smith <jane.smith@example.com>'
    date: string; // E.g. 'Fri, 7 Mar 2025 19:57:14 +0100'
    from: string; // E.g. 'Jane Smith <jane.smith@example.com>'
    id: string; // E.g. '19571f833b735dba'
    internalDate: string; // E.g. '1741373834000'
    parent_id: string; // E.g. '981085a6-fced-4925-8387-a8698809e94b'
    private: string; // E.g. 'true'
    subject: string; // E.g. 'Re: Retain RAG update'
    threadId: string; // E.g. '19571872093c1544'
    to: string; // E.g. 'John Doe <john.doe@example.com>'
    user_id: string; // E.g. '1234567890'
  };
}

export interface RaptorTranscriptSourceNode extends RaptorSourceBaseNode {
  metadata: MetadataBase & {
    file_path: string;
    file_name: string;
    file_size: number;
    creation_date: string;
    last_modified_date: string;
    content_type: string;
    md5_hash: string;
    bucket: string;
    user_id: string;
    private: string;
    parent_id: string;
  };
}

export interface RaptorSummarySourceNode extends RaptorSourceBaseNode {
  metadata: MetadataBase & {
    level: number;
  };
}

export type RaptorSourceNode =
  | RaptorTranscriptSourceNode
  | RaptorEmailSourceNode
  | RaptorSummarySourceNode;

export interface RaptorResponse
  extends Record<string, string | null | RaptorSourceNode[] | MetadataBase> {
  response: string;
  error: string | null;
  source_nodes: RaptorSourceNode[];
  metadata: MetadataBase & {
    response_mode: string;
    mode: string;
  };
}

export interface RaptorRequest {
  query: string;
  user_id: string;
  tree_depth: number;
  response_mode: 'compact' | string;
}

export interface RaptorSearchItem {
  id: string;
  text: string;
  score: number;
  metadata: {
    source?: string;
    date?: string;
    title?: string;
    [key: string]: any;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface RaptorSearchSummary {
  query: string;
  response: string;
  sourceCount: number;
  responseMode: string;
  searchDate: string;
  sources?: Array<{
    id: string;
    text: string;
    score: number;
    metadata: Record<string, any>;
  }>;
}

export interface RaptorSearchDetail {
  query: string;
  response: string;
  responseMode: string;
  searchDate: string;
  sources: Array<{
    id: string;
    text: string;
    score: number;
    metadata: Record<string, any>;
  }>;
  metadata: {
    mode: string;
    dates: string[];
    [key: string]: any;
  };
}

export const asRaptorSourceNode = <T extends RaptorSourceNode>(
  source: RaptorSourceNode,
  type: 'transcript' | 'email' | 'summary'
): T | undefined => {
  if (type === 'transcript' && source?.metadata?.file_name) {
    return source as T;
  }

  if (type === 'email' && source?.metadata?.threadId) {
    return source as T;
  }

  if (type === 'summary' && source?.metadata?.level !== undefined) {
    return source as T;
  }
  return undefined;
};
